import { PageHero } from "@/components/PageHero";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import {
    MessageCircle, ArrowRight, Stethoscope, Car, FileSignature,
    Users, AlertCircle, Heart, Sparkles, Check, Building, Bed, Scale, PenLine, Copy, X,
    HeartHandshake, KeyRound,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Hard Conversations — scripts for the talks no one prepares caregivers for. */
/* -------------------------------------------------------------------------- */

const SECTIONS = [
    {
        id: "diagnosis",
        label: "Receiving the diagnosis",
        Icon: Stethoscope,
        tone: "bg-stage_early/40 !border-stage_early",
        accent: "text-stage_early-dark",
    },
    {
        id: "driving",
        label: "The driving talk",
        Icon: Car,
        tone: "bg-coral-light/50 !border-coral-light",
        accent: "text-coral-dark",
    },
    {
        id: "legal",
        label: "MPOA / DNR / POLST",
        Icon: FileSignature,
        tone: "bg-ml_blue-light/60 !border-ml_blue-light",
        accent: "text-ml_blue-dark",
    },
    {
        id: "family",
        label: "Family conflict",
        Icon: Users,
        tone: "bg-stage_middle/40 !border-stage_middle",
        accent: "text-stage_middle-dark",
    },
    {
        id: "memory-care",
        label: "Moving to memory care",
        Icon: Building,
        tone: "bg-stage_late/40 !border-stage_late",
        accent: "text-stage_late-dark",
    },
    {
        id: "hospice",
        label: "The hospice talk",
        Icon: Bed,
        tone: "bg-cream-dark/60 !border-cream-dark",
        accent: "text-teal-dark",
    },
    {
        id: "home-vs-facility",
        label: "Home aide vs. facility",
        Icon: Scale,
        tone: "bg-yellow-soft/50 !border-yellow-soft",
        accent: "text-yellow-dark",
    },
    {
        id: "caregiver-arriving",
        label: "Telling them help is coming",
        Icon: HeartHandshake,
        tone: "bg-teal-soft/60 !border-teal-soft",
        accent: "text-teal-dark",
    },
    {
        id: "taking-away",
        label: "Taking away access (keys, stove, cards)",
        Icon: KeyRound,
        tone: "bg-coral-light/50 !border-coral-light",
        accent: "text-coral-dark",
    },
];

export default function Conversations() {
    const sectionRefs = useRef({});
    const location = useLocation();

    // Deep-link support:
    //   /conversations#driving           — scroll to a section.
    //   /conversations?section=memory-care&prefill=Dad+lives+with+me%2C+near-fall+last+week
    //     — scroll to a section AND seed the "your situation" textarea for that section
    //       so the caregiver doesn't have to retype what they just told Lane.
    // The prefill itself is consumed by ConvSection's useState initializer (via props),
    // which is what satisfies React 19's set-state-in-effect rule.
    const params = new URLSearchParams(location.search);
    const prefillSection = params.get("section") || "";
    const prefillText = params.get("prefill") || "";
    const hashTarget = (location.hash || "").replace("#", "");
    const scrollTarget = prefillSection || hashTarget;

    useEffect(() => {
        if (!scrollTarget) return;
        const el = sectionRefs.current[scrollTarget];
        if (!el) return;
        const t = setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
        return () => clearTimeout(t);
    }, [scrollTarget]);

    const scrollTo = (id) => {
        if (sectionRefs.current[id]) {
            sectionRefs.current[id].scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.replaceState(null, "", `#${id}`);
        }
    };

    return (
        <div data-clarity-mask="true" className="space-y-10 md:space-y-14" data-testid="conversations-page">
            {/* Hero */}
            <PageHero
                kicker="Hard conversations"
                kickerIcon={MessageCircle}
                title="The talks no one prepared you for."
                subtitle={<>Driving. Diagnosis. Legal paperwork. The sibling who won&apos;t help. These conversations are not pleasant, but they protect everyone — and there are gentle ways to start them. Pick the one you&apos;re facing.</>}
            />

            {/* Quick nav (also serves as table of contents) */}
            <nav
                data-testid="conversations-nav"
                className="ml-card p-4 md:p-5 bg-white grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5"
            >
                {SECTIONS.map((s) => (
                    <button
                        key={s.id}
                        onClick={() => scrollTo(s.id)}
                        data-testid={`conv-nav-${s.id}`}
                        className={`rounded-2xl p-3 text-left border-2 border-transparent hover:bg-cream-dark/40 transition-colors`}
                    >
                        <s.Icon strokeWidth={1.6} size={20} className={`${s.accent} mb-1.5`} />
                        <p className="font-medium text-sm md:text-base text-[#1F2624] leading-tight">{s.label}</p>
                    </button>
                ))}
            </nav>

            {/* Diagnosis */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["diagnosis"] = el)}
                meta={SECTIONS[0]}
                title="Receiving the diagnosis"
                subtitle="The first weeks after a dementia diagnosis are a fog. Most families freeze. Here's how to use them."
                whyHard="The shock blurs everything. You'll want to either deny it or fix it. Neither is the right first move. The first move is gentle, accurate information — and a little time."
                blocks={[
                    {
                        label: "Before you say anything",
                        body: [
                            "Get a second opinion only if the first workup skipped imaging or basic labs. Otherwise, trust the diagnosis and move on — you don't have time to lose to disbelief.",
                            "Write down the diagnosis exactly as the doctor said it (Alzheimer's, vascular, Lewy body, frontotemporal, mixed). The type changes what to expect.",
                            "Ask the clinic for one printed page you can hand to family. People absorb less than 20% of what's said in that room.",
                        ],
                    },
                    {
                        label: "Telling your loved one (if they don't already know)",
                        body: [
                            '"The doctor said your memory changes have a name. It\'s called [type]. It explains why things have felt off lately. We\'re going to figure this out together — slowly. You\'re not alone in this."',
                            "Don't overload. Two or three sentences. Then sit with them.",
                            "Expect denial, anger, or a strange calm — all normal. Don't argue. Don't quiz. Don't say \"remember when…\"",
                        ],
                    },
                    {
                        label: "Telling close family",
                        body: [
                            "Tell them the diagnosis, the stage (if you know it), and what you need from them. Be specific — \"I need one weekend a month\" beats \"some help.\"",
                            "Send the printed page. Repeat it in two weeks. They will forget.",
                        ],
                    },
                    {
                        label: "What not to say (to anyone)",
                        body: [
                            "\"It's going to be okay.\" (You don't know that.)",
                            "\"At least it's not cancer.\" (Dementia is terminal, and people know.)",
                            "\"Don't worry, we'll figure it out.\" (Translation: I will figure it out alone.)",
                        ],
                    },
                ]}
                lanePrompt="I just learned my loved one has been diagnosed with dementia. I don't know what to do first. Help me think this through."
            />

            {/* Driving */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["driving"] = el)}
                meta={SECTIONS[1]}
                title="The driving talk"
                subtitle="This is the conversation caregivers postpone the most — and the one that prevents the most harm."
                whyHard="Driving is freedom. Taking the keys feels like taking adulthood itself. They will be angry. They will say you're overreacting. Do it anyway. One crash undoes everything you're trying to protect."
                blocks={[
                    {
                        label: "Before the conversation",
                        body: [
                            "Get the doctor on your side. Ask them — in front of your loved one — to write 'do not drive' on a prescription pad. Their authority absorbs the conflict; you stop being the bad guy.",
                            "Many states allow anonymous physician reports to the DMV. Look up yours. The DMV letter that revokes the license is far less painful than a family argument.",
                            "Decide what you'll do with the car. Sell it, move it, disable the battery. Keys in a drawer is not enough.",
                        ],
                    },
                    {
                        label: "How to open it (gentle script)",
                        body: [
                            '"Dad, I love you. I have to say something hard and I need you to hear me out. Dr. [name] and I have been talking about your driving. The diagnosis means your reaction time isn\'t what it used to be, even on days you feel fine. We\'re going to stop driving. I will get you wherever you need to go — appointments, the grocery store, your friend\'s house. You will not be stuck at home. I promise."',
                            "Pause. Let them react. Don't fill the silence.",
                        ],
                    },
                    {
                        label: "When they push back",
                        body: [
                            "\"I've been driving for 60 years.\" → \"You're a great driver. This isn't about skill. The disease changes the brain's response time. That's what changed, not you.\"",
                            "\"I'll only drive to the store.\" → \"I know that feels safe. The problem is the one trip that doesn't go the way we plan. I can't take that risk with you.\"",
                            "\"You're treating me like a child.\" → \"I am treating you like someone I love who has a serious diagnosis. That's different.\"",
                        ],
                    },
                    {
                        label: "After",
                        body: [
                            "Set up rides for the first 2 weeks — daughter, neighbor, Lyft, a senior van service. Empty calendar = resentment.",
                            "Expect the topic to come back up. The disease will make them forget the conversation. Don't re-litigate; just gently restate: \"The doctor and I decided — we're not driving anymore. I've got the keys. Where do you need to go?\"",
                        ],
                    },
                ]}
                lanePrompt="I need to have the driving conversation with my loved one. Walk me through it step by step."
            />

            {/* Legal */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["legal"] = el)}
                meta={SECTIONS[2]}
                title="MPOA / DNR / POLST"
                subtitle="The legal paperwork no one wants to do — and the one thing that protects your loved one's wishes when they can no longer speak."
                whyHard="Talking about medical power of attorney, do-not-resuscitate orders, and end-of-life paperwork feels like accelerating a death you're trying to slow. It isn't. It's the opposite — it's the only way to make sure what they want is what happens."
                blocks={[
                    {
                        label: "Know the difference (one minute)",
                        body: [
                            "MPOA (Medical Power of Attorney): names the person who makes medical decisions when your loved one can't. Anyone over 18 should have one. Free, signed at the bedside, often notarized.",
                            "DNR (Do Not Resuscitate): instructs medical staff not to start CPR if the heart stops. Signed by a doctor. Must be on file with the clinic and posted at home.",
                            "POLST / MOLST (Physician Orders for Life-Sustaining Treatment): a doctor's order covering CPR, ventilator, feeding tube, and hospital transfer. Travels with the patient. Required in many states for hospice and EMS to honor wishes.",
                            "Advance directive / living will: written wishes about life-prolonging care. Doesn't replace MPOA or POLST — it informs them.",
                        ],
                    },
                    {
                        label: "When to bring it up",
                        body: [
                            "As early as possible — ideally just after diagnosis, while your loved one can still articulate what they want.",
                            "Not at the bedside in a crisis. Not after a hospital scare. On a quiet afternoon when they're tracking well.",
                        ],
                    },
                    {
                        label: "Opening script",
                        body: [
                            '"Mom, the diagnosis means we should write a few things down while you can tell me exactly what you want. I don\'t want to ever guess. Can we sit down Saturday and go through it? It\'s mostly paperwork — but it\'s your voice, on paper, for the days you might not be able to speak for yourself."',
                            "Frame it as their voice protected, not their death prepared.",
                        ],
                    },
                    {
                        label: "Walk through these questions, in order",
                        body: [
                            "Who do you want making medical decisions if you can't speak? (MPOA)",
                            "If your heart stops, do you want CPR? (DNR)",
                            "If you can't swallow, do you want a feeding tube? For how long?",
                            "Where do you want to die? Home? Hospice facility? Hospital?",
                            "What does a good day look like to you, even at the end?",
                        ],
                    },
                    {
                        label: "Getting it signed",
                        body: [
                            "MPOA + advance directive: a free template from your state's Attorney General site is sufficient. Notarize if your state requires it. Bank notaries are free.",
                            "POLST: ask your loved one's primary care doctor to complete it during the next visit. Bring it pre-filled to save time.",
                            "Hand a copy to: your loved one's doctor, the hospital, every adult child, and the person named as MPOA. Take photos. Cloud-storage them.",
                        ],
                    },
                ]}
                lanePrompt="I need to talk to my loved one about advance directives — MPOA, DNR, POLST. How do I start that conversation?"
            />

            {/* Family conflict */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["family"] = el)}
                meta={SECTIONS[3]}
                title="Family conflict & difficult decisions"
                subtitle="Siblings who won't help. Family who judge. Guilt that won't leave. You are not making this up — this is the hardest part of caregiving."
                whyHard="The disease pulls family closer or pushes it apart, rarely in-between. Old roles come back — the favorite child, the one who left, the peacekeeper. You will feel judged by people who do nothing, and guilty for things that aren't your fault. None of this is unique to you. All of it is solvable, or at least survivable."
                blocks={[
                    {
                        label: "When a sibling says \"let me know how I can help\" but never follows up",
                        body: [
                            "\"Let me know\" is a script that puts the work back on you. Replace it with specific, time-boxed, easy-to-say-yes-to asks.",
                            "Examples that work:",
                            "• \"Can you handle Mom's pharmacy refill calls this month? It's 3 or 4 calls.\"",
                            "• \"Can you cover Saturday so I can sleep? 8am to 6pm. I'll pre-make her meals.\"",
                            "• \"Can you fund 8 hours of respite care next week? About $200. Venmo works.\"",
                            "Always include a deadline: \"Tell me by Sunday which one you can take.\"",
                        ],
                    },
                    {
                        label: "When family members judge your choices",
                        body: [
                            "\"You should put her in a facility.\" → \"I've thought about it. Here's why we're choosing home for now. I'll let you know when that changes.\"",
                            "\"You shouldn't put her in a facility.\" → \"This isn't abandonment. It's the level of care she needs that I can't safely provide. The decision is made.\"",
                            "You don't owe anyone — especially the family member who isn't there day to day — a debate. State your decision, give the one-sentence reason, move on.",
                        ],
                    },
                    {
                        label: "When you're the sole caregiver and resentment is building",
                        body: [
                            "Resentment is a signal, not a flaw. It means you are over-spent and unappreciated. Both are addressable.",
                            "Write down — once — exactly what you do in a week. Hours, tasks, costs. Send it to your siblings. Not as a guilt trip, as a fact sheet. People can't fix what they can't see.",
                            "Ask, in writing: \"Looking at this, what can each of you take?\" Written requests are harder to ignore than spoken ones.",
                            "If they still don't show up, stop asking them. Spend the energy on paid help instead. It will hurt for six months and then you'll feel free.",
                        ],
                    },
                    {
                        label: "When the guilt is loud (it will be)",
                        body: [
                            "Guilt shows up when you place your loved one in care, when you take a weekend off, when you yell, when you forget something, when you feel relief on a quiet day. All of it is normal.",
                            "Try this: \"Would I judge a friend doing exactly what I'm doing right now?\" If no, you don't get to judge yourself either.",
                            "Therapy helps. Caregiver support groups (Alzheimer's Association runs free virtual ones, weekly) help more than people expect.",
                        ],
                    },
                    {
                        label: "When you have to make a hard call alone",
                        body: [
                            "Decisions on hospice, feeding tubes, full-time care — these often fall to the primary caregiver because no one else will choose.",
                            "Use this frame: \"What would [loved one] choose, on their best day, for their worst day?\"",
                            "Not what you want for them. Not what your siblings want. What they would choose if they could see clearly.",
                            "Document the decision in a short note: \"On [date], I decided X because Y. The doctor agreed.\" That note will protect you from second-guessing — your own and others'.",
                        ],
                    },
                ]}
                lanePrompt="My siblings aren't helping with our parent's care and I'm exhausted. Help me figure out what to do."
            />

            {/* Moving to memory care */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["memory-care"] = el)}
                meta={SECTIONS[4]}
                title="Moving to memory care"
                subtitle="The decision most caregivers swore they'd never make — until staying home stopped being safe for either of you."
                whyHard={"It feels like breaking a promise. You'll think: \u201CI told them I'd never put them somewhere.\u201D That promise was made before you knew what stage 6 or 7 looks like — wandering at 3am, refusing showers for two weeks, falls. The kindest thing is sometimes the thing you swore you wouldn't do."}
                blocks={[
                    {
                        label: "Signs it's time (be honest)",
                        body: [
                            "You haven't slept through the night in over a month.",
                            "You're called away from work 2+ times a week for crises.",
                            "Falls, near-falls, or wandering episodes are happening — even with the best safety setup.",
                            "You've snapped at them, or yourself, and scared you.",
                            "Your loved one needs help with toileting and your back can't keep doing it.",
                            "Their doctor or social worker has used the word \"unsafe.\"",
                        ],
                    },
                    {
                        label: "Before you tour anywhere",
                        body: [
                            "Get pre-qualified financially — know what Medicaid covers in your state, what private pay looks like, and what their long-term care insurance (if any) actually pays. This decides which places are realistic.",
                            "Ask the neurologist or PCP for a referral letter — many facilities require it for memory care units.",
                            "Tour at least 3. Visit each at lunchtime AND on a weekend evening. Smell the air. Watch the staff with residents who aren't theirs.",
                        ],
                    },
                    {
                        label: "Talking to your loved one",
                        body: [
                            "If they're stage 6 or 7, they may not be able to engage with the decision. That's not abandonment — it's reality. You make the call.",
                            "If they're stage 5 and still tracking: \"Mom, the doctor and I have been talking. We've reached a point where I can't safely keep you at home — I can't lift you when you fall, and I can't be awake all night anymore. We're going to look at a few places together. I'll still see you every day.\"",
                            "Don't promise it's temporary if it isn't. Don't ask permission you can't honor.",
                        ],
                    },
                    {
                        label: "Talking to family who pushes back",
                        body: [
                            "\"You said you'd never do that.\" → \"I said that before I knew what this would look like. The promise I'm keeping is that they're safe and I'm not destroyed.\"",
                            "\"Why don't we just hire more help at home?\" → \"We've priced it. 24/7 home care is $20k+/month. Memory care is $6-8k and it's the right level of care. If you can fund the difference, I'll listen.\"",
                            "\"It'll kill them faster.\" → \"What kills people faster in dementia is a fall at home with no one there. Facilities have fewer falls.\"",
                        ],
                    },
                    {
                        label: "The first two weeks",
                        body: [
                            "Hardest stretch. They will cry, plead, ask to go home, get angry, forget you visited yesterday. Some facilities recommend not visiting for the first week so they adjust. Some don't. Ask their team.",
                            "Don't argue with the disease. \"I know you want to go home. I'll be back tomorrow. I love you.\" Repeat. Leave.",
                            "Expect to cry in the parking lot. Every caregiver does. Then drive home and sleep — for the first time in months.",
                        ],
                    },
                ]}
                lanePrompt="I think I need to move my loved one into memory care. Help me figure out if I'm ready and what to do next."
            />

            {/* The hospice talk */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["hospice"] = el)}
                meta={SECTIONS[5]}
                title="The hospice conversation"
                subtitle="Most families wish they'd started hospice weeks — sometimes months — earlier. The biggest barrier is the word itself."
                whyHard={"\u201CHospice\u201D sounds like giving up. It isn't. It's a whole team — nurse, aide, social worker, chaplain, equipment — that focuses on comfort instead of cure. Medicare covers it 100% for qualifying patients. The catch: you have to ask, and the doctor has to certify, and that conversation feels impossible."}
                blocks={[
                    {
                        label: "How to know it might be time",
                        body: [
                            "Stage 7 by FAST. Limited words, unable to walk without help, can't sit up alone, swallowing changes.",
                            "Two or more ER visits or hospitalizations in 6 months.",
                            "Significant weight loss, recurrent UTIs, pressure sores, or aspiration pneumonia.",
                            "Your loved one would not want aggressive measures (CPR, feeding tube, ventilator) if their heart stopped.",
                        ],
                    },
                    {
                        label: "Starting it with the doctor",
                        body: [
                            "\"I want to ask about hospice. We're at the point where comfort matters more than fighting. Would they qualify? And if not now, what would qualify them?\"",
                            "Doctors often don't bring it up first — out of habit, training, or hope. You may need to.",
                            "Ask for a palliative care consult if hospice isn't a fit yet — it's the step before, and it brings extra symptom-management support.",
                        ],
                    },
                    {
                        label: "Telling family",
                        body: [
                            "\"We're starting hospice. This doesn't mean she's dying tomorrow — many people are on hospice for months. It means the focus shifts to keeping her comfortable and at peace, and we get a whole team to help.\"",
                            "Then list what hospice brings: a nurse 1-3x/week, an aide for bathing 2-5x/week, equipment delivered free, medications covered, 24/7 phone line, chaplain if you want one, bereavement support for the family.",
                            "Family resistance usually softens once they hear \"a nurse will visit her at home.\"",
                        ],
                    },
                    {
                        label: "What hospice will not do",
                        body: [
                            "Provide 24/7 care. You still need a primary caregiver at home, or memory-care placement.",
                            "Speed anything up. They don't withhold food, water, or medication that brings comfort. \"Comfort care\" means just that.",
                            "Force a DNR. You can choose hospice and still want CPR — though most don't, because it doesn't make sense at this stage.",
                        ],
                    },
                    {
                        label: "Mistakes families regret",
                        body: [
                            "Waiting until the last 48 hours. The team can't deliver much value in 48 hours. Earlier = more support, more help, less suffering.",
                            "Not asking about \"continuous care\" — a hospice benefit where a nurse can stay in the home for 8+ hours during a crisis.",
                            "Not setting up a hospital bed in the living room. Sleeping somewhere shared in the last weeks matters more than people expect.",
                        ],
                    },
                ]}
                lanePrompt="I think it's time to talk about hospice for my loved one. Help me think through this and what to say to the family."
            />

            {/* Home aide vs. facility */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["home-vs-facility"] = el)}
                meta={SECTIONS[6]}
                title="In-home aide vs. facility — the decision"
                subtitle="There is no universally right answer. There is a right answer for your family. Here's how to find it without spiraling."
                whyHard="Both options come with guilt. Keeping them home feels like the loving choice — until it's destroying your sleep, your marriage, and your savings. Moving them feels like abandonment — until you visit and see them safer, eating, and not alone. The honest answer is usually one before the other, in a sequence."
                blocks={[
                    {
                        label: "The 6 questions that decide it",
                        body: [
                            "1) Is your loved one safe at home right now, with current help? (Falls, wandering, stove, medication)",
                            "2) Are you sleeping at least 6 hours a night?",
                            "3) Have you had a full day off in the last month?",
                            "4) Can you afford home care at the level needed for the next 12 months?",
                            "5) Does your loved one have a strong preference, AND the cognitive ability to hold one?",
                            "6) Does someone need to be physically lifted or repositioned more than twice a day?",
                            "If you answer 'no' to 1-3 OR 'yes' to 6, facility is usually the right answer.",
                        ],
                    },
                    {
                        label: "What in-home care actually looks like",
                        body: [
                            "$28-40/hr in most US markets for licensed home health aides.",
                            "8 hours/day, 5 days = $40k-60k/year. 24/7 = $200k+/year.",
                            "Aides are wonderful but they leave at the end of their shift. Night-time wandering, sundowning, falls — those still hit you.",
                            "Best for: middle-stage caregivers who need a break, not 24/7 coverage.",
                        ],
                    },
                    {
                        label: "What memory care actually looks like",
                        body: [
                            "$6-8k/month average in the US (more in coastal cities, less rural).",
                            "Includes housing, meals, 24/7 staff, medication management, activities, secured wandering-safe layout.",
                            "Best for: late-middle and late-stage residents who need consistent, structured care.",
                            "Skilled nursing (~$10-12k/mo) only if there are medical needs the memory-care wing can't handle.",
                        ],
                    },
                    {
                        label: "Talking to your loved one about the decision",
                        body: [
                            "If they can engage: bring 2 options on paper. \"I've looked at two places. I want to take you to see them. We'll decide together.\" Even if you ultimately decide, the visit matters.",
                            "If they can't engage: don't pretend it's a shared decision. Make the call. Bring them in. Set up their room with their things before they arrive.",
                        ],
                    },
                    {
                        label: "The order most families end up using",
                        body: [
                            "Stage 4-5: in-home aide a few days a week. You keep being primary.",
                            "Stage 5-6: adult day program + aide. Buys you working hours.",
                            "Stage 6-7: memory care or skilled nursing. Hospice eventually layers on top.",
                            "If you skip steps, that's fine. If you can't afford a step, talk to your county's Area Agency on Aging — Medicaid waivers exist in most states.",
                        ],
                    },
                ]}
                lanePrompt="I'm trying to decide between an in-home aide and a memory care facility. Help me think through this for my situation."
            />

            {/* Telling them help is coming */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["caregiver-arriving"] = el)}
                meta={SECTIONS[7]}
                title="Telling your loved one a caregiver is coming"
                subtitle="The first day a stranger walks into the house is harder than people expect. How you frame it in the days before changes everything."
                whyHard="Most people with dementia don't think they need help. Saying 'I'm hiring someone to help take care of you' lands as a loss of independence — sometimes as an insult. The win is to frame the caregiver as your help, not theirs, and to start small so the first impression is positive."
                blocks={[
                    {
                        label: "Reframe who the help is for",
                        body: [
                            "Don't say: \"You need help.\" That's the trigger phrase.",
                            "Do say: \"I need help. The house is a lot. Mary is going to come on Tuesdays and Thursdays so I can get a break and we can spend better time together.\"",
                            "Or: \"The doctor wants extra eyes on us for a while — Mary will come by a couple times a week.\"",
                            "This is honest. Caregiving is wearing you out. You DO need the help. Saying so out loud also softens the resistance.",
                        ],
                    },
                    {
                        label: "Pick a non-scary title for the new person",
                        body: [
                            "\"Caregiver\", \"home health aide\", and \"nurse\" can feel medical and threatening.",
                            "Try: \"friend\", \"helper\", \"companion\", \"housekeeper\", \"the nice lady I met at church\". Use whatever lands.",
                            "Match the title to what the aide is mostly doing in the first weeks. Companion who keeps Mom company > clinical aide who bathes her.",
                        ],
                    },
                    {
                        label: "Start small on the very first visit",
                        body: [
                            "Day 1 should NOT include bathing, dressing, or anything intimate. That comes after trust.",
                            "Day 1 IS: 1–2 hours, with you home, having tea or a snack, watching a show together, taking a short walk.",
                            "You leave the room briefly (\"I'm going to fold laundry, be right back\") and let them get used to each other.",
                            "If it goes well, extend by 30 minutes next visit. Build slowly.",
                        ],
                    },
                    {
                        label: "When they refuse to let the person in",
                        body: [
                            "Don't fight at the door. Try: \"Mary is just here to keep me company while I make lunch. Come sit with us.\"",
                            "If a hard no: cancel that visit. Try a different aide, a different day, a different framing. Don't burn the relationship.",
                            "Sometimes the introduction works better outside the home — \"let's meet Mary at the cafe\" — for the first one.",
                            "Once they've had a positive interaction, the aide showing up at home is much easier.",
                        ],
                    },
                    {
                        label: "When they accuse the caregiver later",
                        body: [
                            "Stolen items, breaking things, \"strangers in my house\" — extremely common in middle stages. The brain fills in gaps with confabulation.",
                            "Don't argue: \"Mary didn't steal it.\" Their brain says she did.",
                            "Do: \"That's so frustrating. Let me help look.\" Find or replace the item. Move on.",
                            "Document the incident in your behavior tracker — patterns matter for the care team. Investigate, of course, but assume confabulation first.",
                        ],
                    },
                    {
                        label: "Mistakes families regret",
                        body: [
                            "Hiring full-day aide on day one. Start with 2–4 hours, twice a week.",
                            "Not being home the first 2–3 visits. Your presence makes the transition feel safe.",
                            "Letting the aide and your loved one figure it out without coaching. Tell the aide about routines, favorite shows, what NOT to discuss, names of people, the loved one's preferred title.",
                        ],
                    },
                ]}
                lanePrompt="A caregiver is starting next week and I'm worried about how to tell my loved one. Help me plan the first visit and what to say."
            />

            {/* Taking away access */}
            <ConvSection
                refCb={(el) => (sectionRefs.current["taking-away"] = el)}
                meta={SECTIONS[8]}
                title="Taking away access (stove, cards, alcohol, internet)"
                subtitle="Driving gets its own talk because it's the most visible loss. But there are five or six others — sometimes harder — that hit before stage 6."
                whyHard={"Every item you take away is independence. Removing the wrong one too early can be cruel; waiting too long can be dangerous. The honest move is to remove what's truly unsafe, leave what isn't, and never make it about \"you can't\" — always about \"this changed\"."}
                blocks={[
                    {
                        label: "The kitchen / stove",
                        body: [
                            "Signs it's time: leaving burners on, putting plastic on a hot stove, forgetting they put something in the oven.",
                            "Don't say: \"You can't cook anymore.\" Do say: \"I'm doing the cooking now — sit and keep me company.\"",
                            "Quietly: install a stove-knob lock or a knob-removal cover (≈$30, both common brands work). Pull the gas / unplug the electric range when you're out.",
                            "Replace with: a small toaster oven they CAN use safely (auto-shutoff). Microwaves with simple buttons. Pre-made meals in the fridge they can heat.",
                            "Tell other family before you do it — relatives showing up and shaming you for \"taking the stove away\" is real and miserable.",
                        ],
                    },
                    {
                        label: "Credit cards & cash",
                        body: [
                            "Signs it's time: repeat scam calls believed, large gifts to strangers, duplicate online purchases, missing checks.",
                            "First step: get on the accounts as joint or POA, not after the damage. Same for online logins.",
                            "Don't take the card silently — that's a betrayal moment. Do say: \"The credit card company called about fraud. I'm handling it. I have your card. Here's a debit card with $50 on it for the corner store.\"",
                            "Set up True Link or EverSafe (≈$10–15/mo each) — prepaid debit card you control, flagged-purchase alerts. They feel independent; you can see every charge.",
                            "Freeze credit at all 3 bureaus (free) to stop new accounts being opened.",
                        ],
                    },
                    {
                        label: "Alcohol",
                        body: [
                            "Dementia + alcohol is dangerous: medication interactions, falls, escalated sundowning.",
                            "Cold turkey usually backfires — agitation, demands, sneaking. Better: water down the bottle gradually, switch to non-alcoholic beer (most can't tell), \"the doctor changed your medication so you can't have it for a few weeks\".",
                            "Don't shame. The drink isn't the point — the routine is. Keep the glass, the time, the ritual. Just swap what's in it.",
                        ],
                    },
                    {
                        label: "The internet / phone / TV remote",
                        body: [
                            "Signs it's time: repeated late-night calls to family, accidentally calling 911, sending money on the phone, ordering 14 of the same thing on Amazon.",
                            "Don't take the phone away — they'll feel imprisoned. Do: switch them to a simplified phone (RAZ Memory Cell Phone, GrandPad — both have caregiver-controlled contact lists).",
                            "Put a parental-control / ad blocker on the TV remote inputs. Lock down the streaming-purchase settings.",
                            "Remove their Amazon 1-click. Set the laptop / iPad to require your password for any purchase.",
                        ],
                    },
                    {
                        label: "Power tools, firearms, sharp things in the workshop",
                        body: [
                            "Underrated risk. Talk to the rest of the family before you act, especially if firearms are involved — there's grief AND legal nuance.",
                            "Don't argue or remove in front of them. Quietly relocate to a relative's garage. \"Tom is borrowing the chainsaw\" lands soft.",
                            "Firearms: lock to a safe outside the home OR transfer ownership to a family member, per your state's elder-safety laws. Your local sheriff's office can often advise.",
                        ],
                    },
                    {
                        label: "How to talk about it, every time",
                        body: [
                            "Take the agency back without naming the loss. \"I'm taking the cards / keys / stove / drinks today\" — declarative, calm, kind, not asking permission.",
                            "Don't explain it ten times. Their brain won't hold the reason. Repeat the action: \"I'm the one doing it now.\"",
                            "Expect grief. Sometimes anger. Sometimes relief. All three are normal — sometimes from the same person, same day.",
                            "Then — and this is the actually important part — replace the thing with something they CAN still do. Independence isn't gone. It's smaller. Find them a new safe job, daily ritual, hobby they can still own.",
                        ],
                    },
                ]}
                lanePrompt="I need to take away [the keys / stove / credit cards / alcohol] from my loved one. Help me figure out how to do it and what to say."
            />

            {/* Footer note */}
            <footer className="ml-card p-6 md:p-7 bg-cream-dark/40 !border-cream-dark text-sm md:text-base text-[#1F2624] leading-relaxed">
                <p className="flex items-start gap-2.5">
                    <Heart strokeWidth={1.6} size={18} className="text-teal-dark shrink-0 mt-0.5" />
                    <span>
                        These scripts are starting points, not the right thing for every family.
                        Adapt the language to how you actually talk. The goal is to start — not
                        to say it perfectly. If a conversation goes badly, you can have it again.
                    </span>
                </p>
                <p className="mt-3 text-xs text-[#4C5551] pl-7">
                    Educational support only. Not medical or legal advice. For state-specific
                    legal documents, consult an elder law attorney or your state Attorney General&apos;s site.
                </p>
            </footer>
        </div>
    );
}

/* ----- Helpers ----- */

function ConvSection({ refCb, meta, title, subtitle, whyHard, blocks, lanePrompt }) {
    const { id, Icon, tone, accent } = meta;
    const sid = getSessionId();
    const location = useLocation();
    const CONTEXT_KEY = `mlcn_conv_ctx_${sid}_${id}`;
    const DRAFT_KEY = `mlcn_conv_draft_${sid}_${id}`;

    const [drafting, setDrafting] = useState(false);
    const [draft, setDraft] = useState(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            return saved ? { text: saved, copied: false } : null;
        } catch { return null; }
    });
    const [draftOpen, setDraftOpen] = useState(false);
    // useState initializer runs synchronously on mount — perfect for reading the
    // URL prefill from Lane (?section=<this id>&prefill=…) and merging it with
    // anything the caregiver already typed previously. Side-effect (writing to
    // localStorage) is fine here because this is mount-time, not an effect.
    const [customContext, setCustomContext] = useState(() => {
        let existing = "";
        try { existing = localStorage.getItem(CONTEXT_KEY) || ""; } catch { /* noop */ }
        let prefill = "";
        try {
            const p = new URLSearchParams(location.search);
            if (p.get("section") === id) prefill = (p.get("prefill") || "").trim();
        } catch { /* noop */ }
        if (!prefill) return existing;
        const merged = existing && !existing.includes(prefill)
            ? `${existing.trim()}\n\n${prefill}`
            : (existing || prefill);
        try { if (sid) localStorage.setItem(CONTEXT_KEY, merged); } catch { /* noop */ }
        return merged;
    });

    // Autosave context + draft text per section so caregivers don't lose work mid-thought.
    useEffect(() => {
        try {
            if (customContext) localStorage.setItem(CONTEXT_KEY, customContext);
            else localStorage.removeItem(CONTEXT_KEY);
        } catch { /* ignore */ }
    }, [customContext, CONTEXT_KEY]);
    useEffect(() => {
        try {
            if (draft && draft.text) localStorage.setItem(DRAFT_KEY, draft.text);
            else localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
    }, [draft, DRAFT_KEY]);

    const generateDraft = async () => {
        setDrafting(true);
        setDraft(null);
        setDraftOpen(true);
        try {
            const r = await api.post("/ai/draft-conversation", {
                section_id: id,
                session_id: sid,
                custom_context: customContext,
            });
            setDraft({ text: r.data.draft || "", copied: false });
        } catch {
            toast.error("Couldn't draft this right now. Try again in a moment.");
            setDraftOpen(false);
        } finally {
            setDrafting(false);
        }
    };

    const writeMyOwn = () => {
        // Open the modal so the user can write entirely from scratch (or pick up an autosaved draft).
        if (!draft) setDraft({ text: "", copied: false });
        setDraftOpen(true);
    };

    const copy = async () => {
        if (!draft) return;
        try {
            await navigator.clipboard.writeText(draft.text);
            setDraft({ ...draft, copied: true });
            toast.success("Copied — your draft is saved for next time.");
            setTimeout(() => setDraft(d => d ? { ...d, copied: false } : d), 1800);
        } catch {
            toast.error("Couldn't copy. Select the text manually.");
        }
    };

    const clearAll = () => {
        setDraft(null);
        setCustomContext("");
        try {
            localStorage.removeItem(CONTEXT_KEY);
            localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
        toast.success("Cleared.");
    };

    return (
        <section
            id={id}
            ref={refCb}
            data-testid={`conv-section-${id}`}
            className={`ml-card p-6 md:p-8 ${tone} scroll-mt-24`}
        >
            <div className="flex items-start gap-3.5 mb-4">
                <Icon strokeWidth={1.6} size={26} className={`${accent} shrink-0 mt-1`} />
                <div>
                    <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-[#12776F]">
                        {title}
                    </h2>
                    <p className="mt-2 text-base md:text-lg text-[#4C5551] leading-relaxed">
                        {subtitle}
                    </p>
                </div>
            </div>

            {/* Why this is hard */}
            <div className="rounded-2xl bg-white/80 p-4 md:p-5 mb-5 border border-[#E2E8F0]/60">
                <p className={`text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5 ${accent}`}>
                    <AlertCircle strokeWidth={1.8} size={13} /> Why this is hard
                </p>
                <p className="text-sm md:text-base text-[#1F2624] leading-relaxed">{whyHard}</p>
            </div>

            {/* Blocks */}
            <div className="space-y-5">
                {blocks.map((b, i) => (
                    <div key={i} data-testid={`conv-block-${id}-${i}`}>
                        <h3 className="text-base md:text-lg font-heading font-medium text-[#1F2624] mb-2 flex items-center gap-2">
                            <Check strokeWidth={2} size={16} className={accent} />
                            {b.label}
                        </h3>
                        <ul className="space-y-2 pl-7">
                            {b.body.map((line, j) => (
                                <li key={j} className="text-sm md:text-base text-[#1F2624] leading-relaxed">
                                    {line}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {/* Actions row: Draft (AI), Write my own, Talk to Lane */}
            <div className="mt-7 pt-5 border-t border-white/60 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={generateDraft}
                    disabled={drafting}
                    data-testid={`conv-draft-${id}`}
                    className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-5 py-2.5 font-medium transition-all min-h-[44px] text-sm md:text-base disabled:opacity-60"
                >
                    <PenLine strokeWidth={1.7} size={16} />
                    {drafting ? "Drafting…" : "Draft this for me"}
                </button>
                <button
                    type="button"
                    onClick={writeMyOwn}
                    data-testid={`conv-write-own-${id}`}
                    className="inline-flex items-center gap-2 bg-white hover:bg-cream-dark border-2 border-[#E2E8F0] text-[#1F2624] rounded-2xl px-5 py-2.5 font-medium transition-all min-h-[44px] text-sm md:text-base"
                >
                    <PenLine strokeWidth={1.7} size={16} />
                    Write my own
                </button>
                <Link
                    to={`/chat?prompt=${encodeURIComponent(lanePrompt)}`}
                    data-testid={`conv-ask-lane-${id}`}
                    className="inline-flex items-center gap-2 bg-white hover:bg-cream-dark border-2 border-teal text-teal-dark rounded-2xl px-5 py-2.5 font-medium transition-all min-h-[44px] text-sm md:text-base"
                >
                    <Sparkles strokeWidth={1.7} size={16} />
                    Talk to Lane about this
                    <ArrowRight strokeWidth={1.8} size={16} />
                </Link>
            </div>

            {/* Draft modal */}
            {draftOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid={`conv-draft-modal-${id}`}
                    className="fixed inset-0 z-50 bg-[#1F2624]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setDraftOpen(false)}
                >
                    <div
                        className="ml-card bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-7"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div className="flex items-start gap-3">
                                <Sparkles strokeWidth={1.6} size={20} className="text-teal-dark shrink-0 mt-1" />
                                <div>
                                    <h3 className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                                        {draft && !drafting ? "Edit, then copy" : "Drafting a message"}
                                    </h3>
                                    <p className="text-sm text-[#4C5551] mt-0.5">
                                        Add your specifics, draft for you, edit, copy and send.
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDraftOpen(false)}
                                aria-label="Close"
                                data-testid={`conv-draft-close-${id}`}
                                className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                            >
                                <X size={16} strokeWidth={1.8} />
                            </button>
                        </div>

                        {/* Optional caregiver-supplied context */}
                        <div className="mb-4">
                            <label
                                htmlFor={`conv-context-${id}`}
                                className="block text-sm font-medium text-[#1F2624] mb-1.5"
                            >
                                Anything specific you want Lane to keep? <span className="text-[#6E7A74] font-normal">(optional)</span>
                            </label>
                            <textarea
                                id={`conv-context-${id}`}
                                data-testid={`conv-context-${id}`}
                                className="w-full min-h-[88px] rounded-2xl border border-[#E2E8F0] bg-white p-3 text-sm text-[#1F2624] leading-relaxed font-sans focus:outline-none focus:border-teal"
                                placeholder="e.g. Mom calls me 'sweetie', dad refuses to talk about hospice, my sibling lives in Texas, doc said no driving last Tuesday…"
                                value={customContext}
                                onChange={(e) => setCustomContext(e.target.value)}
                            />
                            <p className="mt-1 text-xs text-[#6E7A74]">Lane keeps your words; the rest comes from your intake.</p>
                        </div>

                        {drafting && (
                            <div className="py-10 text-center text-[#4C5551]">
                                <Sparkles strokeWidth={1.6} size={28} className="mx-auto text-teal-dark animate-pulse mb-2" />
                                Drafting a message for your situation…
                            </div>
                        )}

                        {!drafting && draft && (
                            <>
                                <label htmlFor={`conv-draft-text-${id}`} className="block text-sm font-medium text-[#1F2624] mb-1.5">
                                    Your message <span className="text-[#6E7A74] font-normal">(edit freely)</span>
                                </label>
                                <textarea
                                    id={`conv-draft-text-${id}`}
                                    data-testid={`conv-draft-text-${id}`}
                                    className="w-full min-h-[220px] md:min-h-[280px] rounded-2xl border border-[#E2E8F0] bg-cream/60 p-4 text-sm md:text-base text-[#1F2624] leading-relaxed font-sans focus:outline-none focus:border-teal"
                                    placeholder="Write your message here, or tap 'Draft this for me' below."
                                    value={draft.text}
                                    onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                                />
                                <div className="mt-4 flex flex-wrap gap-2.5 items-center justify-end">
                                    <span className="text-xs text-[#6E7A74] mr-auto italic">
                                        {draft.text || customContext ? "Saved automatically" : ""}
                                    </span>
                                    {(draft.text || customContext) && (
                                        <button
                                            type="button"
                                            onClick={clearAll}
                                            data-testid={`conv-draft-clear-${id}`}
                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:bg-cream-dark rounded-2xl px-3 py-2"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={generateDraft}
                                        data-testid={`conv-draft-regen-${id}`}
                                        className="inline-flex items-center gap-2 bg-white hover:bg-cream-dark border border-[#E2E8F0] text-[#1F2624] rounded-2xl px-4 py-2 font-medium text-sm transition-colors"
                                    >
                                        <Sparkles strokeWidth={1.6} size={14} />
                                        {draft.text.trim() ? "Re-draft with these specifics" : "Draft this for me"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={copy}
                                        disabled={!draft.text.trim()}
                                        data-testid={`conv-draft-copy-${id}`}
                                        className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark disabled:opacity-50 text-white rounded-2xl px-4 py-2 font-medium text-sm transition-colors"
                                    >
                                        {draft.copied ? <Check strokeWidth={2} size={14} /> : <Copy strokeWidth={1.7} size={14} />}
                                        {draft.copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                                <p className="mt-4 text-xs text-[#4C5551] italic">
                                    These are starting words, not a script. Make them sound like you.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
